import { DropdownOption } from "../common/ReactDropdown";
import FortnoxClient, { JsonResponse } from "./client";

export interface FilterValues {
  startDate: Date;
  endDate: Date;
  companyType?: string;
  companyId?: string;
  projectId?: string;
}

export enum InvoiceRowType {
  Text,
  Article,
  EmptyRow,
}

export interface InvoiceTextRow {
  rowType: InvoiceRowType.Text;
  text: string;
}

export interface InvoiceArticleRow {
  rowType: InvoiceRowType.Article;
  articleNo: string;
  quantity: number;
  name: string;
  unitName: string;
  unitPrice: number;
  totalPrice: number;
  houseWork: boolean;
  houseWorkType: HouseWorkType;
  askedTaxReductionAmount: number | null;
}

export enum HouseWorkType {
  Construction,
  Electricity,
  GlassMetalWork,
  GroundDrainageWork,
  Masonry,
  PaintingWallpapering,
  HVAC,
  MajorApplianceRepair,
  MovingServices,
  ITServices,
  Cleaning,
  TextileClothing,
  Snowplowing,
  Gardening,
  Babysitting,
  OtherCare,
  OtherCosts,
  Cooking,
  Tutoring,
  SolarCells,
  StorageSelfProducedElectricity,
  ChargingStationElectricVehicle,
  HomeMaintenance,
  Furnishing,
  TransportationServices,
  WashingAndCareOfClothing,
  EmptyHousework,
  Blank,
}

export interface FortnoxInvoiceEmptyRow {
  rowType: InvoiceRowType.EmptyRow;
}

export type InvoiceRow =
  | InvoiceTextRow
  | InvoiceArticleRow
  | FortnoxInvoiceEmptyRow;

export enum InvoiceStatus {
  Draft,
  Sent,
  Error,
}

export interface InvoiceData {
  id: number;
  companyType: number;
  customerNo: string;
  customerName: string;
  rows: InvoiceRow[];
  priceSum: number;
  priceSumIncludingVAT: number;
  houseWorkSum: number;
  houseWorkSumIncludingVAT: number;
  taxReductionSum: number;
  status: InvoiceStatus;
  exportError: string | null;
  createTaxReductionError: string | null;
  fortnoxInvoiceUrl: string | null;
}

export enum InvoiceCreatorStateStatus {
  Loading,
  Ready,
  CreatingInvoices,
}

export interface InvoiceCreatorState {
  id: string;
  status?: InvoiceCreatorStateStatus;
  invoices: InvoiceData[];
}

class FortnoxInvoiceCreatorClient extends FortnoxClient {
  constructor(apiEndpoint: string) {
    super(apiEndpoint);
  }

  async getInvoices(
    filterValues: FilterValues,
  ): Promise<JsonResponse<InvoiceCreatorState>> {
    const response = await this.fetchJson<InvoiceCreatorState>(
      this.getApiUrl("GetInvoices", undefined, [
        ["startDate", filterValues.startDate.toISOString()],
        ["endDate", filterValues.endDate.toISOString()],
        ["companyType", filterValues.companyType ?? ""],
        ["companyId", filterValues.companyId ?? ""],
        ["projectIds", filterValues.projectId ?? ""], // if necessary it's possible to specify multiple project ids by separating them with a comma
      ]),
    );

    return response;
  }

  async getCompanyProjects(companyId: string): Promise<DropdownOption[]> {
    const response = await this.fetch(
      this.getApiUrl("GetCompanyProjects", undefined, [
        ["companyId", companyId],
      ]),
    );

    if (response.status != 200) {
      console.log("Request failed", response);
      throw new Error(`Unexpected request response: ${response.status}`);
    }

    const data = await response.json();
    return data;
  }

  async exportInvoices(
    stateId: string,
    invoiceIds: number[],
    invoiceDate: Date,
  ): Promise<JsonResponse<InvoiceCreatorState>> {
    const response = await this.fetchJson<InvoiceCreatorState>(
      this.getApiUrl("ExportInvoices", stateId, [
        ["invoiceDate", invoiceDate.toISOString()],
      ]),
      {
        method: "POST",
        body: JSON.stringify({ invoiceIds }),
      },
    );

    return response;
  }
}

export default FortnoxInvoiceCreatorClient;
